import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.full_name}`);
      const next = params.get("next");
      if (next) nav(next);
      else if (u.role === "admin") nav("/admin");
      else if (u.role === "vendor") nav("/vendor");
      else nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="login-page">
      <h1 className="font-display text-4xl mb-2">Welcome back</h1>
      <p className="text-gray-600 mb-8">Log in to continue shopping.</p>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#F48E2E] hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input data-testid="login-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
            <button
              type="button"
              data-testid="login-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full rounded-full h-11 bg-[#F48E2E] hover:bg-[#D97815]">
          {busy ? "Signing in…" : "Log in"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-center text-gray-600">
        New here? <Link to="/register" className="text-[#F48E2E]">Create an account</Link>
      </div>
      <div className="mt-2 text-sm text-center text-gray-600">
        Are you a seller? <Link to="/vendor-register" className="text-[#F48E2E]">Become an online exhibitor</Link>
      </div>
    </div>
  );
}
